import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import ThresholdPage from "@/pages/ThresholdPage";
import OfferPage from "@/pages/OfferPage";
import ActiveBirthPage from "@/pages/ActiveBirthPage";
import BirthArtPage from "@/pages/BirthArtPage";
import MumsBabiesPage from "@/pages/MumsBabiesPage";
import ThaiMassagePage from "@/pages/ThaiMassagePage";
import MotherSupportPage from "@/pages/MotherSupportPage";
import MenopauseSupportPage from "@/pages/MenopauseSupportPage";
import FirstPeriodPage from "@/pages/FirstPeriodPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import WomensCirclePage from "@/pages/WomensCirclePage";
import About from "@/pages/About";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import Contact from "@/pages/Contact";
import AdminLetter from "@/pages/AdminLetter";
import AdminBooklet from "@/pages/AdminBooklet";
import AdminBookings from "@/pages/AdminBookings";
import Privacy from "@/pages/Privacy";
import Unsubscribe from "@/pages/Unsubscribe";
import NotFound from "@/pages/NotFound";
import LegalPage from "@/pages/LegalPage";
import Withdrawal from "@/pages/Withdrawal";
import AdminGuides from "@/pages/AdminGuides";
import AdminEnquiries from "@/pages/AdminEnquiries";
import RitualConsultationPage from "@/pages/RitualConsultationPage";
import { CookieConsent } from "@/components/CookieConsent";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

// Public pages exist in English at /... and in Dutch at /nl/...
const PUBLIC_ROUTES = [
  ["/", <Home />],
  ["/thresholds/:slug", <ThresholdPage />],
  ["/offerings/active-birth", <ActiveBirthPage />],
  ["/offerings/birth-art", <BirthArtPage />],
  ["/offerings/mums-babies-circle", <MumsBabiesPage />],
  ["/offerings/thai-massage", <ThaiMassagePage />],
  ["/offerings/one-to-one-mothers", <MotherSupportPage />],
  ["/offerings/menopause-support", <MenopauseSupportPage />],
  ["/offerings/first-period", <FirstPeriodPage />],
  ["/offerings/ritual-consultation", <RitualConsultationPage />],
  ["/womens-circle", <WomensCirclePage />],
  ["/offerings/:slug", <OfferPage />],
  ["/about", <About />],
  ["/journal", <Journal />],
  ["/journal/:slug", <JournalPost />],
  ["/contact", <Contact />],
  ["/privacy", <Privacy />],
  ["/terms", <LegalPage pageKey="terms" />],
  ["/cancellation-refund-policy", <LegalPage pageKey="cancellation" />],
  ["/withdrawal", <Withdrawal />],
];

// Journal articles were re-slugged in September 2026 — keep the old URLs alive.
const JOURNAL_REDIRECTS = {
  "/journal/birth-as-a-rite-of-passage": "/journal/birth-as-a-rite-of-passage-reclaiming-the-power-of-birth",
  "/journal/menopause-a-cycle-of-change": "/journal/menopause-a-time-of-change-and-becoming",
  "/journal/remembering-rhythm": "/journal/the-wisdom-of-thresholds",
  "/journal/postpartum-recovery": "/journal/postpartum-recovery-gentle-support-for-new-mothers",
  "/journal/dutch-maternity-care": "/journal/understanding-dutch-maternity-care-from-pregnancy-to-the-kraamweek",
  "/nl/journal/birth-as-a-rite-of-passage": "/nl/journal/geboorte-als-overgangsritueel-de-kracht-van-geboorte-herwaarderen",
  "/nl/journal/menopause-a-cycle-of-change": "/nl/journal/een-tijd-van-verandering-en-worden",
  "/nl/journal/postpartum-recovery": "/nl/journal/postpartum-herstel-zachte-ondersteuning-voor-nieuwe-moeders",
  "/nl/journal/dutch-maternity-care": "/nl/journal/nederlandse-geboortezorg-begrijpen-van-zwangerschap-tot-de-kraamweek",
};

// Legacy URLs from earlier versions of the site → current canonical pages.
const LEGACY_REDIRECTS = {
  "/active-birth": "/offerings/active-birth",
  "/birth-preparation": "/offerings/active-birth",
  "/birth-art": "/offerings/birth-art",
  "/thai-massage": "/offerings/thai-massage",
  "/zwangerschapsmassage-schiedam": "/offerings/thai-massage",
};

// WordPress-era URLs (indexed by Google) → canonical pages.
// Served as real HTTP 301s by the dev-server middleware (scripts/wp-redirects.cjs
// + craco.config.js); these React routes are the fallback when the page is
// reached without that layer. Keep in sync with scripts/wp-redirects.cjs.
const WP_LEGACY_REDIRECTS = {
  "/blog": "/journal",
  "/2025/12/postpartum-recovery-gentle-guidance-for-new-mothers": "/journal/postpartum-recovery-gentle-support-for-new-mothers",
  "/2025/07/menopause-a-cycle-of-change-and-becoming": "/journal/menopause-a-time-of-change-and-becoming",
  "/2025/12/kraamzorg-and-the-dutch-maternity-care-system-guide-for-families-in-the-netherlands": "/journal/understanding-dutch-maternity-care-from-pregnancy-to-the-kraamweek",
  "/natuurlijke-geboorte-workshop-schiedam-voorbereiding-bevalling": "/offerings/active-birth",
  "/begeleiding-postnataal-voor-moeders-mums-babies-circle": "/offerings/mums-babies-circle",
  "/product/thai-massage-for-pregnant-women-or-postpartum-recovery": "/offerings/thai-massage",
  "/schiedam-workshops-for-women-wellbeing-birth-embodiment-womens-den": "/",
  "/product/womens-circle-gathering-in-schiedam": "/womens-circle",
  "/contact-irida-hysenbelli-workshops-for-women": "/contact",
  "/product-category/birth-recovery-support": "/thresholds/pregnancy-motherhood",
  "/product/120-mins-thai-massage-for-pregnant-women-or-postpartum-recovery": "/offerings/thai-massage",
  "/voorbereiding-op-de-bevalling-schiedam-birth-preparation-class": "/offerings/active-birth",
  "/begeleiding-perimenopauze-voor-vrouwen-womens-den": "/offerings/menopause-support",
  "/womens-coach-schiedam-womens-wellbeing-coaching-irida-hysenbelli": "/about",
  "/vrouwencirkel-schiedam-womens-circle-bij-womens-den": "/womens-circle",
  "/2025/10/menstruatiecyclus-bijhouden-voor-gezondheid": "/thresholds/first-bleed",
};

// React Router does not match "/blog/" for path "/blog" — register both variants.
const slashVariants = (map) =>
  Object.entries(map).flatMap(([from, to]) => {
    const bare = from.replace(/\/+$/, "");
    return [[bare, to], [`${bare}/`, to]];
  });

// Fallback for /author/admin/ when reached without the server layer (which
// answers a true HTTP 410 Gone).
const Gone410 = () => {
  useEffect(() => {
    document.title = "Gone — Women's Den";
  }, []);
  return (
    <main data-testid="gone-410" className="flex min-h-screen items-center justify-center bg-cream px-6">
      <p className="max-w-md text-center font-serif text-2xl leading-relaxed text-ink">
        This page has been permanently removed.
      </p>
    </main>
  );
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <ScrollManager />
          <Navbar />
          <Routes>
            {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            {Object.entries(JOURNAL_REDIRECTS).map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            {slashVariants(WP_LEGACY_REDIRECTS).map(([from, to]) => (
              <Route key={`wp:${from}`} path={from} element={<Navigate to={to} replace />} />
            ))}
            <Route path="/author/admin" element={<Gone410 />} />
            <Route path="/author/admin/" element={<Gone410 />} />
            {PUBLIC_ROUTES.map(([path, el]) => (
              <Route key={path} path={path} element={el} />
            ))}
            {PUBLIC_ROUTES.map(([path, el]) => (
              <Route key={`nl:${path}`} path={path === "/" ? "/nl" : `/nl${path}`} element={el} />
            ))}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/admin/letter" element={<AdminLetter />} />
            <Route path="/admin/booklet" element={<AdminBooklet />} />
            <Route path="/admin/bookings" element={<AdminBookings />} />
            <Route path="/admin/guides" element={<AdminGuides />} />
            <Route path="/admin/enquiries" element={<AdminEnquiries />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
          <CookieConsent />
          <Toaster position="bottom-center" toastOptions={{ style: { background: "#2C2A26", color: "#FAF8F5", border: "none" } }} />
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
